import { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "@/hooks/useLanguage";
import { Badge } from "@/components/ui/badge";
import OptimizedImage from "@/components/ui/optimized-image";
import { formatBlogDate } from "@/hooks/useBlogPosts";
import { ChevronLeft, ChevronRight } from "lucide-react";
import tripMountains from "@/assets/trip-mountains.webp";

interface BlogPost {
  id: string;
  title: string;
  excerpt: string;
  slug: string;
  image: string | null;
  category: string;
  published_at: string | null;
}

interface BlogCarousel3DProps {
  posts: BlogPost[];
}

const BlogCarousel3D = ({ posts }: BlogCarousel3DProps) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const navigate = useNavigate();
  const { getLocalizedPath } = useLanguage();

  const goNext = useCallback(() => {
    setActiveIndex((prev) => (prev + 1) % posts.length);
  }, [posts.length]);

  const goPrev = useCallback(() => {
    setActiveIndex((prev) => (prev - 1 + posts.length) % posts.length);
  }, [posts.length]);

  // Auto-advance every 5s
  useEffect(() => {
    const timer = setInterval(goNext, 5000);
    return () => clearInterval(timer);
  }, [goNext]);

  if (posts.length === 0) return null;

  const getCardStyle = (index: number) => {
    const diff = index - activeIndex;
    // Normalize to handle wrapping
    const normalizedDiff =
      diff > posts.length / 2
        ? diff - posts.length
        : diff < -posts.length / 2
        ? diff + posts.length
        : diff;

    const absD = Math.abs(normalizedDiff);

    if (absD > 2) {
      return {
        transform: "scale(0.6) translateX(0)",
        opacity: 0,
        zIndex: 0,
        pointerEvents: "none" as const,
      };
    }

    const translateX = normalizedDiff * 320;
    const scale = 1 - absD * 0.15;
    const rotateY = normalizedDiff * -12;
    const opacity = 1 - absD * 0.3;
    const z = 10 - absD * 5;

    return {
      transform: `translateX(${translateX}px) scale(${scale}) perspective(1000px) rotateY(${rotateY}deg)`,
      opacity,
      zIndex: z,
      pointerEvents: "auto" as "auto" | "none",
    };
  };

  return (
    <div className="relative w-full">
      {/* Carousel container */}
      <div className="relative h-[380px] sm:h-[420px] flex items-center justify-center overflow-hidden">
        {posts.map((post, index) => (
          <div
            key={post.id}
            className="absolute w-[300px] sm:w-[360px] transition-all duration-500 ease-out cursor-pointer"
            style={getCardStyle(index)}
            onClick={() => {
              if (index === activeIndex) {
                navigate(getLocalizedPath(`/blog/${post.slug}`));
              } else {
                setActiveIndex(index);
              }
            }}
          >
            <article className="bg-card rounded-xl overflow-hidden border border-border shadow-lg hover:shadow-xl transition-shadow duration-300">
              <div className="relative aspect-[16/10] overflow-hidden">
                <OptimizedImage
                  src={post.image || tripMountains}
                  alt={post.title}
                  className="hover:scale-105"
                  containerClassName="h-full"
                  width={360}
                  height={225}
                />
                <Badge className="absolute top-3 left-3 bg-primary/90 hover:bg-primary text-primary-foreground text-xs z-10">
                  {post.category}
                </Badge>
              </div>
              <div className="p-4">
                <h3 className="font-semibold text-base mb-2 line-clamp-2 group-hover:text-primary transition-colors">
                  {post.title}
                </h3>
                <p className="text-muted-foreground text-sm line-clamp-2 mb-3">
                  {post.excerpt}
                </p>
                <div className="text-xs text-muted-foreground">
                  {formatBlogDate(post.published_at)}
                </div>
              </div>
            </article>
          </div>
        ))}
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-center gap-4 mt-4">
        <button
          onClick={goPrev}
          className="p-2 rounded-full bg-card border border-border hover:bg-accent transition-colors"
          aria-label="Previous"
        >
          <ChevronLeft className="h-5 w-5" />
        </button>

        <div className="flex gap-2">
          {posts.map((_, i) => (
            <button
              key={i}
              onClick={() => setActiveIndex(i)}
              className={`w-2 h-2 rounded-full transition-all duration-300 ${
                i === activeIndex
                  ? "bg-primary w-6"
                  : "bg-muted-foreground/30 hover:bg-muted-foreground/50"
              }`}
              aria-label={`Slide ${i + 1}`}
            />
          ))}
        </div>

        <button
          onClick={goNext}
          className="p-2 rounded-full bg-card border border-border hover:bg-accent transition-colors"
          aria-label="Next"
        >
          <ChevronRight className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
};

export default BlogCarousel3D;
